<script lang="ts" setup>
/* eslint-disable import-x/no-duplicates -- #layers/base/public is also imported for feature flag registration side effects. */
import { builtinBlocks } from 'virtual:monospace-builtin-blocks';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useHead } from '#app/composables/head';
import { useRoute } from '#app/composables/router';
import { useAppConfig } from '#app/config';
import { useAuth } from '#layers/access/public';
import { useFeatureFlagKeyboardShortcuts } from '#layers/base/public';
import { registerBuiltinBlocks } from '#layers/block-engine/public';
import { useNuxtUiLocale } from '#layers/ui/public';
import { useAppearance } from '~/composables/use-appearance';

// Register feature flags. This import needs to remain intact.
import '#layers/base/public';
/* eslint-enable import-x/no-duplicates */

registerBuiltinBlocks(builtinBlocks);

const appConfig = useAppConfig();
const route = useRoute();

// The onboarding steps swoop up + blur out one after the other (out-in). Driven
// here rather than per-page so the sequence stays defined in one place; other
// layouts opt out and get no page transition.
const pageTransition = computed(() =>
	route.meta.layout === 'onboarding'
		? { name: 'onboarding-step', mode: 'out-in' as const, appear: true }
		: false,
);

const { locale } = useI18n();
const { currentUser } = useAuth();
const nuxtUiLocale = useNuxtUiLocale();

useHead({
	htmlAttrs: {
		lang: locale,
		dir: computed(() => nuxtUiLocale.value.dir ?? 'ltr'),
	},
});

useAppearance(() => currentUser.value?.appearance);

useFeatureFlagKeyboardShortcuts();
</script>

<template>
	<NuxtRouteAnnouncer />
	<AiAppContextProvider />
	<DApp :locale="nuxtUiLocale" :tooltip="appConfig.tooltip">
		<AppLoading />
		<OnboardingExitCover />
		<NuxtLayout>
			<NuxtPage :transition="pageTransition" />
		</NuxtLayout>
	</DApp>
</template>
